import { resetFilterRequest } from '../api/filter';
import { renderCollectionHeader } from '../collection-header/index.js';
import {
  FILTER_DESKTOP_WRAPPER_SELECTOR,
  FILTER_MOBILE_BUTTON_BACK,
  FILTER_MOBILE_BUTTON_BACK_CLASS,
  FILTER_MOBILE_BUTTON_CLOSE,
  FILTER_MOBILE_BUTTON_CLOSE_CLASS,
  FILTER_MOBILE_BUTTON_HIDDEN,
  FILTER_MOBILE_FULL_HEIGHT_CLASS,
  FILTER_MOBILE_FULL_HEIGHT_SELECTOR,
  FILTER_MOBILE_WRAPPER_SELECTOR,
  FILTER_OPTION_CONTENT_INNER,
  FILTER_OPTION_CONTENT_INNER_SCROLL_BAR,
  FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS,
  FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS,
  FILTER_OPTION_SEARCH_OPTION_SELECTOR,
  FILTER_OPTION_SELECTOR,
  FILTER_OPTION_TITLE_OPENING_CLASS,
  FILTER_TAG_KEY,
  FILTER_TREE_MOBILE_BUTTON_CLEAR,
  FILTER_TREE_MOBILE_TOOL_BAR,
  FILTER_TREE_RESET_DELAY,
  FILTER_TREE_STATE_KEY,
  SHOW_MORE_TYPES,
} from '../constants/filter-tree.js';
import { renderRecommendationForSearchPage } from '../dynamicBundle/index.js';
import {
  clearAllParamHistory,
  debounce,
  deleteQueryParamsHistory,
  getParamsHistory,
  getThemeSettings,
  getUrlScheme,
  initShortenMaps,
  isMobile,
  isMobileWidth,
  isSearchPage,
  isTabletPortraitMaxWidth,
  removeQueryParamsHistory,
  requestIdleCallback,
  rewriteUrlFromParams,
  scrollToTop,
  simpleMd5,
  stripHtml,
  translateByKey,
  updateValuesOptions,
} from '../utils';
import { lazyLoadOptionBox, renderFullOptionBox, renderOptionBox } from './option-box';
import {
  getCollectionIdByHandleKey,
  getCollectionsTagFromSlugKey,
  saveCollectionIdByHandle,
} from './option-collection.js';
import {
  generateFilterDataForList,
  lazyLoadOptionList,
  removeFilterSelectedClass,
  renderFullOptionList,
  renderOptionList,
} from './option-list';
import {
  lazyLoadOptionMultiLevelCollection,
  renderFullOptionMultiLevelCollection,
  renderOptionMultiLevelCollection,
} from './option-multi-level-collection';
import {
  lazyLoadOptionMultiLevelTag,
  renderFullOptionMultiLevelTag,
  renderOptionMultiLevelTag,
} from './option-multi-level-tag';
import { generateFilterDataForRange, renderRangeSlider } from './option-range-slider';
import { lazyLoadOptionRating, renderFullOptionRating, renderOptionRating } from './option-rating';
import { lazyLoadOptionSwatch, renderFullOptionSwatch, renderOptionsSwatch } from './option-swatch';
import { renderRefineBy } from './refine-by';
import {
  blockFilterOpening,
  getStickyConfig,
  getStickyStatus,
  loadStickyFilterTreeExpandContent,
  loadStickyFilterTreeHorizontalContent,
  loadStickyFilterTreeHorizontalPanel,
  loadStickyFilterTreeMobileExpandContent,
  stickyHorizontal,
  stickyMobile,
  stickyVertical,
} from './sticky';
import { applyTranslationsToFilterOptions } from './translation';

const LIMIT_FIRST_LOAD = 30;
const COLLAPSE_STATE = 'collapseState';

/**
 * Renders the filter tree UI based on the filter tree options state.
 * Calls lazyLoadFilterTree() to initialize loading the options.
 * Renders the filter options that are active.
 * For list options, checks if they are in viewport and renders or lazy loads accordingly.
 * @type {import('./index.d').handleFilterTree}
 */
export function handleFilterTree(context) {
  // Store previous filter tree options for comparison
  let previousFilterOptions = null;
  // Store timeout ID to prevent redundant resets
  let filterResetTimeoutId = null;

  const { shortenUrlParamList } = getFilterSettings(context);
  initShortenMaps({ shortenUrlParamList });

  syncActiveFiltersToContext(context);

  window.addEventListener(
    'resize',
    debounce(function () {
      handleFilterTreeResponsive(context);
    }, 100)
  );

  context.render(function renderFilterTreeOnStateChanged() {
    if (!context.state.filterTree || !context.state.filterTree?.options) return;

    // Add collection filter tree change detection
    const { filterTreeMobileStyle } = getFilterSettings(context);
    const isMobileStyle2 =
      filterTreeMobileStyle === 'style2' || filterTreeMobileStyle === 'style2-fullwidth';

    /**
     * Checks if mobile filter tree needs to be reset when filter options change
     * Resets mobile filter visibility when a selected option no longer exists or has no values
     */
    const resetMobileFilter = (
      context,
      filterResetTimeoutId,
      previousFilterOptions,
      isMobileStyle2
    ) => {
      // Early return if we're not on mobile or not using Style2
      if (!isMobile(context?.app?.generalSettings?.isTabletPortraitMax) || !isMobileStyle2) {
        return filterResetTimeoutId;
      }

      // Get the currently selected filter option ID
      const [getFilterOptionIdMobileSelected] = context.useContextState(
        'filterOptionIdMobileSelected',
        ''
      );
      const selectedData = getFilterOptionIdMobileSelected() || {};
      const selectedFilterOptionId = selectedData.key;

      // If we had a selected filter option and filter tree options exist
      if (
        selectedFilterOptionId &&
        previousFilterOptions &&
        previousFilterOptions.length > 0 &&
        context.state.filterTree.options.length > 0
      ) {
        // Check if the selected filter option still exists AND has values in the new filter tree
        const selectedOptionExists = context.state.filterTree.options.some(
          (option) =>
            option.filterOptionId === selectedFilterOptionId &&
            option.values &&
            option.values.length > 0
        );

        // If the selected filter option no longer exists, reset mobile filter visibility
        if (!selectedOptionExists) {
          // Clear any pending reset to prevent redundant calls
          if (filterResetTimeoutId) {
            clearTimeout(filterResetTimeoutId);
          }
          // Schedule a new reset
          filterResetTimeoutId = setTimeout(() => {
            backFilterTreeMobile(context);
            filterResetTimeoutId = null; // Clear the ID after execution
          }, FILTER_TREE_RESET_DELAY);
        }
      }

      return filterResetTimeoutId;
    };

    filterResetTimeoutId = resetMobileFilter(
      context,
      filterResetTimeoutId,
      previousFilterOptions,
      isMobileStyle2
    );

    // Store current filter options for next comparison
    previousFilterOptions = [...(context.state.filterTree?.options || [])];

    lazyLoadFilterTree(context);

    // register when search/filter in search page
    if (isSearchPage()) {
      renderRecommendationForSearchPage(context);
      import(
        /* webpackChunkName: 'volume-bundle' */
        '../volume-bundle'
      ).then((module) => {
        const { renderVolumeBundleForSearchPage } = module;
        renderVolumeBundleForSearchPage(context);
      });
    }

    const { collection_handle, collection_id, page } =
      window.boostWidgetIntegration?.generalSettings || {};

    if (page === 'collection') {
      saveCollectionIdByHandle(collection_handle, collection_id);
    }

    renderCollectionHeader(context);
  }, []);

  context.render(() => {
    renderRefineBy(context);

    const { filterTreeMobileStyle } = getFilterSettings(context);

    if (!['style3', 'style3-fullwidth'].includes(filterTreeMobileStyle)) return;
    if (!isMobile(context?.app?.generalSettings?.isTabletPortraitMax)) return;

    const selectedFilter = getSelectedFilter(context);
    const titleEl = context.$('.boost-sd__filter-tree-vertical-toolbar-middle');

    if (!titleEl) return;

    if (selectedFilter.length === 0) {
      titleEl.textContent = translateByKey(context, 'refineMobile', 'Refine by');
      return;
    }

    const lastFilter = selectedFilter[selectedFilter.length - 1];
    if (lastFilter?.data?.key) {
      const option = getFilterOptionById(context, lastFilter.data.key);
      if (option?.label) {
        titleEl.textContent = option.label;
      }
    }
  }, ['filter']);

  window.addEventListener('afterRenderHtmlForFilter', function (e) {
    const [getCurrentFocus] = context.useContextState(FILTER_TREE_STATE_KEY.CURRENT_FOCUS, '');
    const currentFocus = getCurrentFocus();
    const currentFocusElement = document.getElementById(currentFocus);

    const { filterLayout, filterTreeHorizontalStyle } = getFilterSettings(context);

    const { paginationType = 'default' } =
      getThemeSettings(context)?.additionalElements?.pagination || {};

    // Add check for search input
    const isSearching = document.activeElement?.matches(FILTER_OPTION_SEARCH_OPTION_SELECTOR);

    // Don't change focus if user is searching
    if (isSearching) {
      return;
    }

    if (paginationType === 'default') {
      if (filterLayout === 'horizontal' && filterTreeHorizontalStyle !== 'style-expand') {
        currentFocusElement
          ?.closest('.boost-sd__filter-option')
          ?.querySelector('.boost-sd__filter-option-title')
          ?.focus();
      } else if (isEnterOrSpaceKeyPressed(context)) {
        // Try to focus saved element first
        if (currentFocusElement) {
          currentFocusElement.setAttribute('tabindex', '0');
          currentFocusElement.focus();

          // Verify focus was successful, fallback if not
          setTimeout(() => {
            if (document.activeElement !== currentFocusElement) {
              currentFocusElement?.closest('.boost-sd__filter-tree-wrapper')?.focus();
            }
          }, 50);
        } else {
          // Fallback: focus filter tree wrapper
          document.querySelector('.boost-sd__filter-tree-wrapper')?.focus();
        }
      } else {
        currentFocusElement?.closest('.boost-sd__filter-tree-wrapper')?.focus();
      }
    }
  });
}

/**
 * @type {import('./index.d').keepStateMultipleLevelOptionItems}
 */
export function keepStateMultipleLevelOptionItems(context) {
  const { keepToggleState } = getFilterSettings(context);
  if (!keepToggleState) return;

  const [getCollapseStateMultipleLevel] = context.useContextState('collapseStateMultipleLevel', {});
  const _getCollapseStateMultipleLevel = getCollapseStateMultipleLevel();
  const keysMultipleLevel = Object.keys(_getCollapseStateMultipleLevel);

  keysMultipleLevel.forEach((key) => {
    handleCollapse(context, _getCollapseStateMultipleLevel[key], true);
  });
}

/**
 * @type {import('./index.d').getSelectedKeysFilter}
 */
export const getSelectedKeysFilter = (context) => {
  const [getFilter] = context.useContextState('filter', []);
  let selectedFilters = getFilter();
  selectedFilters = Array.isArray(selectedFilters) ? selectedFilters : [];

  const selectedKeys = selectedFilters.reduce(
    (obj, val) => ({ ...obj, [`${val.data.key}-${val.data.value}`.toLowerCase()]: true }),
    {}
  );

  return selectedKeys;
};

/**
 * @type {import('./index.d').getSelectedFilter}
 */
export const getSelectedFilter = (context) => {
  const [getFilter] = context.useContextState('filter', []);
  let selectedFilters = getFilter();
  selectedFilters = Array.isArray(selectedFilters)
    ? selectedFilters?.filter((f) => f && f.data)
    : [];

  return selectedFilters;
};

/**
 * @type {import('./index.d').isFilterOptionSupported}
 */
export const isFilterOptionSupported = (displayType) => {
  return ['list', 'box', 'swatch', 'range', 'rating', 'multi_level_collections'].includes(
    displayType
  );
};

/**
 * @type {import('./index.d').renderFilterOption}
 */
export const renderFilterOption = async (context, dom, option, limit) => {
  switch (true) {
    case option.displayType === 'multi_level_collections':
      return renderOptionMultiLevelCollection(context, dom, option, limit);

    case option.displayType === 'list' && option.filterType === 'multi_level_tag':
      return renderOptionMultiLevelTag(context, dom, option, limit);

    case option.displayType === 'list' && option.filterType !== 'multi_level_tag':
      return await renderOptionList(context, dom, option, limit);

    case option.displayType === 'box':
      return await renderOptionBox(context, dom, option, limit);

    case option.displayType === 'swatch':
      return await renderOptionsSwatch(context, dom, option, limit);

    case option.displayType === 'rating':
      return renderOptionRating(context, dom, option, limit);

    case option.displayType === 'range':
      return renderRangeSlider(context, dom, option);

    default:
      console.log('renderFilterOption not cover', option);
      break;
  }
};

/**
 * @type {import('./index.d').renderFullyFilterOption}
 */
export const renderFullyFilterOption = (context, dom, option) => {
  switch (true) {
    case option.displayType === 'multi_level_collections':
      renderFullOptionMultiLevelCollection(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'list' && option.filterType === 'multi_level_tag':
      renderFullOptionMultiLevelTag(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'list' && option.filterType !== 'multi_level_tag':
      renderFullOptionList(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'box':
      renderFullOptionBox(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'swatch':
      renderFullOptionSwatch(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'rating':
      renderFullOptionRating(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'range':
      renderRangeSlider(context, dom, option);
      break;

    default:
      console.log(`renderFullyFilterOption not cover type ${option.displayType}`);
      break;
  }
};

/**
 * @type {import('./index.d').renderLazyFilterOption}
 */
export const renderLazyFilterOption = (context, dom, option) => {
  switch (true) {
    case option.displayType === 'multi_level_collections':
      lazyLoadOptionMultiLevelCollection(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'list' && option.filterType === 'multi_level_tag':
      lazyLoadOptionMultiLevelTag(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'list' && option.filterType !== 'multi_level_tag':
      lazyLoadOptionList(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'box':
      lazyLoadOptionBox(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'swatch':
      lazyLoadOptionSwatch(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'rating':
      lazyLoadOptionRating(context, dom, option.filterOptionId, option);
      break;

    case option.displayType === 'range':
      renderRangeSlider(context, dom, option);
      break;

    default:
      console.log('renderLazyFilterOption not cover', option.displayType);
      break;
  }
};

/**
 * @type {import('./index.d').buttonClearOrClearAll}
 */
const buttonClearOrClearAll = (context, type = 'clearAll', optionId) => {
  const buttonClear = document.createElement('button');
  buttonClear.type = 'button';
  buttonClear.id = 'boost-sd__button-toolbar-mobile-clear';
  buttonClear.className = 'boost-sd__button boost-sd__button--clear';

  if (type === 'clear' && optionId) {
    buttonClear.textContent = translateByKey(context, 'clear', 'Clear');
    buttonClear.setAttribute(
      'data-metadata',
      `{ "action": { "resetFilterOption": { "key": "${optionId}" }}}`
    );
  } else {
    buttonClear.textContent = translateByKey(context, 'clearAll', 'Clear All');
    buttonClear.setAttribute('data-metadata', '{ "action": { "clearAllFilter": "" }}');
  }

  return buttonClear;
};

/**
 * @type {import('./index.d').getFilterOptionIdEncodeMetaField}
 */
function getFilterOptionIdEncodeMetaField(optionId) {
  return optionId.replace(/::/g, '__');
}

/**
 * @type {import('./index.d').reCalcWidthFilterOptionHorizontal}
 */
function reCalcWidthFilterOptionHorizontal(filterTreeHorizontalStyle, filterLayout) {
  // issue: PFSN-53876 re-calc current position when open style1, style2 horizontal - adapt left or right
  if (['style1', 'style2'].includes(filterTreeHorizontalStyle) && filterLayout === 'horizontal') {
    const filterOptions = document.querySelectorAll(
      `#boost-sd__filter-tree-wrapper .boost-sd__filter-option--${filterTreeHorizontalStyle}.boost-sd__filter-option--horizontal`
    );
    if (filterOptions?.length > 0) {
      const WIDTH_WRAPPER_OPTION_STYLE1_HORIZONTAL = 240; // px
      const WIDTH_WRAPPER_OPTION_STYLE2_HORIZONTAL = 532; // px

      const widthFilterOptionStyle =
        filterTreeHorizontalStyle === 'style1'
          ? WIDTH_WRAPPER_OPTION_STYLE1_HORIZONTAL
          : WIDTH_WRAPPER_OPTION_STYLE2_HORIZONTAL;

      filterOptions?.forEach((filterOption) => {
        const currentFilterOption = filterOption?.getBoundingClientRect();

        const wrapper = filterOption?.querySelector(
          `.boost-sd__filter-option-itemwrapper--${filterTreeHorizontalStyle}`
        );
        if (
          window.innerWidth / 2 < currentFilterOption?.left &&
          window.innerWidth - currentFilterOption?.left < widthFilterOptionStyle
        ) {
          filterOption?.setAttribute('style', 'position: relative');
          wrapper?.setAttribute('style', 'right: 0');
        }
      });
    }
  }
}

/**
 * @type {import('./index.d').handleFilterTreeResponsive}
 */
export const handleFilterTreeResponsive = (context) => {
  const mobileParent = context.$(FILTER_MOBILE_WRAPPER_SELECTOR);
  const desktopParent = context.$(FILTER_DESKTOP_WRAPPER_SELECTOR);
  const { filterLayout, filterTreeHorizontalStyle, filterTreeMobileStyle } =
    getFilterSettings(context);
  if (!mobileParent || !desktopParent) {
    return;
  }

  const isMobileSize = isMobile(context.app.generalSettings.isTabletPortraitMax);
  if (isMobileSize && desktopParent && desktopParent?.style?.display !== 'none') {
    desktopParent.style.display = 'none';
  }

  if (!isMobileSize && desktopParent && desktopParent?.style?.display === 'none') {
    closeMobileFilterTree(context);
    desktopParent.style.display = 'unset';

    // only render one times
    if (!window.renderFilterTreeFirstLoad) {
      lazyLoadFilterTree(context);
      window.renderFilterTreeFirstLoad = true;
    }
  }

  const isFilterTreeMobileStyle1 = filterTreeMobileStyle === 'style1';
  const isFilterTreeMobileStyle2 =
    filterTreeMobileStyle === 'style2' || filterTreeMobileStyle === 'style2-fullwidth';
  const isFilterTreeMobileStyle3 =
    filterTreeMobileStyle === 'style3' || filterTreeMobileStyle === 'style3-fullwidth';
  const isDesktopHorizontal = !isMobileSize && filterLayout === 'horizontal';
  const filterOptionListHorizontal = '.boost-sd__filter-tree-horizontal-option-list';
  const filterOptionListMobile =
    '#boost-sd_filter-tree-mobile .boost-sd__filter-tree-vertical-option-list';
  const filterOptionWrapper = mobileParent?.querySelector(
    '.boost-sd__filter-option-itemwrapper--full-height'
  );

  if ((isFilterTreeMobileStyle3 || isFilterTreeMobileStyle1) && isMobileSize) {
    addRemoveHiddenClass(context, 'remove', filterOptionListMobile);
  }

  if (isFilterTreeMobileStyle2 && isMobileSize) {
    addRemoveHiddenClass(context, 'add', filterOptionListMobile);
    filterOptionWrapper?.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
  }

  if (isDesktopHorizontal) {
    if (filterTreeHorizontalStyle === 'style-expand') {
      addRemoveHiddenClass(context, 'remove', filterOptionListHorizontal);
    } else {
      addRemoveHiddenClass(context, 'add', filterOptionListHorizontal);
    }
  }
};

/**
 * @type {import('./index.d').addRemoveHiddenClass}
 */
const addRemoveHiddenClass = (context, action, targetElement) => {
  const filterOptionItemWrapper = '.boost-sd__filter-option-itemwrapper';

  if (context.$(targetElement) !== null) {
    Array.from(context.$(targetElement)?.querySelectorAll(filterOptionItemWrapper))?.forEach(
      (e) => {
        if (action === 'add') {
          e?.classList?.add(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
        }
        if (action === 'remove') {
          e?.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
        }
      }
    );
  }
};

/**
 * @type {import('./index.d').getFilterTreeParent}
 */
export const getFilterTreeParent = (context) => {
  if (isMobile(context?.app?.generalSettings?.isTabletPortraitMax)) {
    return context.$(FILTER_MOBILE_WRAPPER_SELECTOR);
  }

  return context.$(FILTER_DESKTOP_WRAPPER_SELECTOR);
};

/**
 * @type {import('./index.d').showMobileFilterTree}
 */
export const showMobileFilterTree = (context) => {
  const { filterTreeMobileStyle } = getFilterSettings(context);
  const filterTreeMobileWrapper = context.$(FILTER_MOBILE_WRAPPER_SELECTOR);
  if (filterTreeMobileWrapper.style.display === 'none') {
    filterTreeMobileWrapper.style.display = 'block';
  } else {
    filterTreeMobileWrapper.style.display = 'none';
  }

  if (filterTreeMobileStyle !== 'style1') {
    document.body.classList.add('boost-sd__g-no-scroll');
  } else {
    toggleFilterTreeIcon(context);
  }

  lazyLoadFilterTree(context);
};

/**
 * @type {import('./index.d').toggleFilterTreeIcon}
 */
export function toggleFilterTreeIcon(context) {
  const isMobileSize = isMobile(context?.app?.generalSettings?.isTabletPortraitMax);
  const filterTreeToggleButton = isMobileSize
    ? context.$('.boost-sd__toolbar-top-mobile .boost-sd__filter-tree-toggle-button')
    : context.$('.boost-sd__filter-tree-toggle-button--expand');
  filterTreeToggleButton?.classList?.toggle('boost-sd__filter-tree-toggle-button--active');

  const { stickyFilterOnMobile, filterTreeMobileStyle } = getFilterSettings(context);
  if (stickyFilterOnMobile) {
    // Get merged configuration for mobile expand sticky call
    const stickyConfig = getStickyConfig('mobile', filterTreeMobileStyle);
    loadStickyFilterTreeMobileExpandContent(context, stickyConfig.content);
  }

  // Toggle tabindex on toolbar elements when the filter button is clicked
  const toolbarElements = document.querySelectorAll('.boost-sd__toolbar [tabindex]');
  function updateTabIndex(element) {
    const tabIndex = element.getAttribute('tabindex');
    if (tabIndex === '0') {
      element.setAttribute('tabindex', '-1');
      filterTreeToggleButton?.removeAttribute('tabindex');
    } else if (tabIndex === '-1') {
      element.setAttribute('tabindex', '0');
      filterTreeToggleButton?.setAttribute('tabindex', '0');
      filterTreeToggleButton?.focus();
    }
  }
  if (toolbarElements.length > 0) {
    toolbarElements.forEach(updateTabIndex);
  }
}

/**
 * @type {import('./index.d').closeMobileFilterTree}
 */
export const closeMobileFilterTree = (context) => {
  const mobile = context.$(FILTER_MOBILE_WRAPPER_SELECTOR);
  if (mobile) {
    mobile.style.display = 'none';
  }
  document.body?.classList?.remove('boost-sd__g-no-scroll');
};

/**
 * @type {import('./index.d').fillFilterTree}
 */
export function fillFilterTree(context) {
  // case state has filter tree data
  if (context?.state?.filterTree?.options) {
    handleFillFilterTree(context);
    return;
  }

  //case state do not have filter tree then trigger when state update filter tree
  context.render(() => {
    if (context.state.filterTree && context.state.filterTree.options) {
      handleFillFilterTree(context);
    }
  }, ['filterTree']);
}

/**
 * @type {import('./index.d').handleFillFilterTree}
 */
const handleFillFilterTree = (context) => {
  const [, setFilter] = context.useContextState('filter', []);

  // Name is showSingleOption but data return from BE hideSingOption
  const { showOutOfStockOption, showSingleOption: hideSingleOption } = getFilterSettings(context);
  const settingOptionValues = {
    showOutOfStockOption: showOutOfStockOption,
    hideSingleOption,
  };

  const options = updateValuesOptions(
    context.state.filterTree.options,
    settingOptionValues,
    context
  );
  const newFilter = fillMissValueForFilterTree(context, options, context.state.filter);
  setFilter(newFilter);
};

/**
 * @type {import('./index.d').flattenTagsToObject}
 */
const flattenTagsToObject = (array) => {
  const result = {};

  array.forEach((item) => {
    result[item.tag] = item.displayName;
    if (item.subTags) {
      Object.assign(result, flattenTagsToObject(item.subTags));
    }
  });

  return result;
};

/**
 * @type {import('./index.d').fillMissValueForFilterTree}
 */
const fillMissValueForFilterTree = (context, options, selectedFilters) => {
  const optionsMap = options.reduce((obj, val) => ({ ...obj, [val.filterOptionId]: val }), {});
  let fillTagDependencies = false;

  const filterTree = selectedFilters.map((selectedFilter) => {
    let { valueDisplay } = selectedFilter.data;
    const { key, value } = selectedFilter.data;

    if (selectedFilter.type) {
      // filter already has data, do not need to fill
      return selectedFilter;
    }

    if (key === FILTER_TAG_KEY) {
      fillTagDependencies = true;
      return {
        data: { label: 'Collection Tag', value, key, valueDisplay: value },
        metaData: { key, value },
        type: 'tag',
      };
    }

    if (optionsMap[key].displayType === 'range') {
      return generateFilterDataForRange(key, value, optionsMap[key]);
    }

    const filterOption = optionsMap[key];
    // fix: BC-1033 - Incorrect refine by element label of multi_level_tag
    if (filterOption.filterType === 'multi_level_tag' && !valueDisplay) {
      const keyDisplayName = flattenTagsToObject(filterOption.values);
      valueDisplay = keyDisplayName[value];
    }

    if (filterOption.filterType === 'multi_level_tag') {
      filterOption.valueDisplay = valueDisplay;
    } else {
      // filterOption.values[0].label use to check this filter have label to display
      if (filterOption.values && filterOption.values.length > 0 && filterOption.values[0].label) {
        const matchValue = filterOption.values.find((v) => String(v.key) === String(value));
        if (matchValue) {
          let { label } = matchValue;
          const { prefix } = filterOption;

          // If have prefix then remove prefix from label
          if (prefix && typeof label === 'string') {
            const cleanPrefix = prefix.replace(/\\/g, '');
            label = label.replace(cleanPrefix, '');
          }

          filterOption.valueDisplay = label;
        } else {
          filterOption.valueDisplay = valueDisplay;
        }
      }
    }
    return generateFilterDataForList(context, key, value, filterOption);
  });

  const collectionFilterOptionId = getCollectionFilterOptionId(options);

  if (fillTagDependencies) {
    const tag = filterTree.find((f) => f.data.key === FILTER_TAG_KEY);
    const collection = filterTree.find((f) => f.data.key === collectionFilterOptionId);
    const tagMetadata = { ...tag.metaData };
    tag.metaData.dependencies = [{ ...collection.metaData }];
    collection.metaData.dependencies = [tagMetadata];
  }

  return filterTree;
};

/**
 * @type {import('./index.d').clearAllFilter}
 */
export function clearAllFilter(context, { preventPushHistory = false }) {
  const [, setFilter] = context.useContextState('filter', []);
  const { isShortenUrlParam } = getFilterSettings(context);
  setFilter([]);
  resetFilterRequest(context);
  clearAllParamHistory({ preventPushHistory, isShortenUrlParam });
  scrollToTop({ isFilter: true });
}

/**
 * @type {import('./index.d').removeFilterFromRefineBy}
 */
export function removeFilterFromRefineBy(context, action) {
  // force true => always call api
  clearFilter(context, action, true);
}

/**
 * @type {import('./index.d').clearFilter}
 */
export function clearFilter(context, action, forceCall = false) {
  const { filterLayout, isShortenUrlParam, stickyFilterOnDesktop, filterTreeHorizontalStyle } =
    getFilterSettings(context);
  let isCall = true;

  // only apply for filter tree desktop, tablet - not apply for mobile.
  if (!forceCall && filterLayout === 'horizontal' && !isTabletPortraitMaxWidth()) {
    isCall = false;
  }

  const filters = action.dependencies ? [action, ...action.dependencies] : [action];
  const [getFilter, setFilter] = context.useContextState('filter', []);
  let newFilterTree = getFilter();

  filters.forEach((filter) => {
    const { key, value, type, displayType } = filter;
    newFilterTree = newFilterTree.filter(
      (ft) =>
        ft && (ft.metaData.key !== key || (ft.metaData.key === key && ft.metaData.value !== value))
    );
    removeFilterSelectedClass(context, key, value, type || displayType);
  });

  removeQueryParamsHistory(filters, { isShortenUrlParam, urlScheme: getUrlScheme() });
  setFilter(newFilterTree);

  // If no filters are left, always reset the filter request.
  if (newFilterTree.length === 0) {
    resetFilterRequest(context);
  } else if (isCall) {
    resetFilterRequest(context);
  }
  if (isCall && filterTreeHorizontalStyle === 'style-expand' && stickyFilterOnDesktop) {
    setTimeout(() => scrollToTop({ isFilter: true, scrollToElement: '.boost-sd-container' }), 0);
  }
}

/**
 * @type {import('./index.d').parseHistoryParams}
 */
export const parseHistoryParams = (context = {}) => {
  const { paramList, sort, pagination } = getParamsHistory();
  const { isShortenUrlParam } = getFilterSettings(context);
  const [, setPage] = context.useContextState('pagination ', {});

  if (sort) {
    const [, setSort] = context.useContextState('sort', {});
    setSort({ sort });
  }

  setPage({ page: pagination.page || 1 });

  syncActiveFiltersToContext(context);
  context.handler(renderRefineBy, 'renderRefineBy')(context);
  rewriteUrlFromParams(
    { paramList, sort, pagination },
    { isShortenUrlParam, urlScheme: getUrlScheme() }
  );
};

/**
 * @type {import('./index.d').syncActiveFiltersToContext}
 */
export const syncActiveFiltersToContext = (context = {}) => {
  const { paramList } = getParamsHistory();
  const listFilters = [];
  paramList.forEach((param) => {
    param.values.forEach((value) => {
      listFilters.push({ data: { key: param.key, value } });
    });
  });
  const [, setFilter] = context.useContextState('filter', []);
  setFilter(listFilters);
};

/**
 * @type {import('./index.d').parseCollectionPath}
 */
export const parseCollectionPath = (context) => {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/collections/')) {
    // slide 2 to remove '' and 'collections'
    const path = pathname.split('/').slice(2);

    if (path.length > 0) {
      const collectionHandle = path[0];
      context.app.generalSettings.collection_id = getCollectionIdByHandleKey(collectionHandle);
      context.app.generalSettings.collection_handle = collectionHandle;
    }

    // have tag in collection
    if (path.length > 1) {
      const collectionTag = getCollectionsTagFromSlugKey(path[1]);
      context.app.generalSettings.collection_tags = [collectionTag];
    } else {
      context.app.generalSettings.collection_tags = null;
    }
  }
};

/**
 * @type {import('./index.d').bindEventSearchOption}
 */
function bindEventSearchOption(context, option, optionDom, filterTreeParent) {
  // bind event input on search option
  const searchOptionByFilterOption = context.$(
    `#boost-sd__search-option-${getFilterOptionIdEncodeMetaField(option.filterOptionId)}`,
    filterTreeParent
  );
  if (searchOptionByFilterOption) {
    searchOptionByFilterOption.addEventListener(
      'input',
      debounce(function (e) {
        // deep copy object
        const _optionCopy = JSON.parse(JSON.stringify(option));
        const value = e.target.value?.toLowerCase();

        // special case -> collection key = displayName
        let key = 'key';
        if (option.filterType === 'collection') {
          key = 'displayName';
        }

        _optionCopy.values = _optionCopy.values.filter((item) =>
          item[key]?.toString()?.toLowerCase()?.includes(value)
        );

        renderFilterOption(context, optionDom, _optionCopy, option.values?.length);
      }, 200)
    );
  }
}

/**
 * @type {import('./index.d').bindingToolbarFilterMobileStyle}
 */
function bindingToolbarFilterMobileStyle(context, option) {
  const [getFilterOptionIdMobileSelected] = context.useContextState(
    'filterOptionIdMobileSelected',
    ''
  );
  const { level, key: currentFilterOptionIdMobile } = getFilterOptionIdMobileSelected();

  // mobile style2 then binding title of toolbar filter selected.
  if (currentFilterOptionIdMobile === option.filterOptionId) {
    // title refine by mobile
    const refineByTitle = context.$(
      '.boost-sd__filter-tree-vertical-toolbar-middle',
      FILTER_MOBILE_WRAPPER_SELECTOR
    );

    if (refineByTitle) {
      refineByTitle.innerHTML = option.label;
    }
  }

  // bind clearAll button to FilterTree Mobile
  const filterSelected = getSelectedFilter(context);
  let buttonClear = '';

  if (level === 1 || !level) {
    buttonClear = buttonClearOrClearAll(context, 'clearAll');
  } else if (level === 2 && currentFilterOptionIdMobile) {
    const hasFilteringCurrentOptionId = filterSelected.find(
      (filter) => filter.metaData?.key === currentFilterOptionIdMobile
    );

    if (hasFilteringCurrentOptionId) {
      buttonClear = buttonClearOrClearAll(context, 'clear', currentFilterOptionIdMobile);
    }
  }

  if (buttonClear) {
    if (filterSelected.length === 0) {
      buttonClear.classList.add('boost-sd__g-hide');
    }

    if (!context.$(FILTER_TREE_MOBILE_BUTTON_CLEAR)) {
      const toolBarMobile = context.$(FILTER_TREE_MOBILE_TOOL_BAR);
      toolBarMobile?.append(buttonClear);
    } else {
      context.$(FILTER_TREE_MOBILE_BUTTON_CLEAR).replaceWith(buttonClear);
    }
  }
}

/**
 * @type {import('./index.d').lazyLoadFilterTree}
 */
export async function lazyLoadFilterTree(context) {
  const { useContextState } = context;
  const [getFilterTreeOption] = useContextState('filterTree', {});

  let options = getFilterTreeOption().options;
  if (!options) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    return lazyLoadFilterTree(context);
  }

  const {
    showOutOfStockOption,
    showSingleOption: hideSingleOption,
    filterLayout,
    filterTreeHorizontalStyle,
    filterTreeMobileStyle,
    stickyFilterOnDesktop,
  } = getFilterSettings(context);
  // update settings Option Values with settings hideSingle option or show out of stock
  const settingOptionValues = {
    showOutOfStockOption,
    hideSingleOption,
  };

  options = updateValuesOptions(options, settingOptionValues, context);

  // calculate width of option horizontally when opening
  if (filterLayout === 'horizontal') {
    reCalcWidthFilterOptionHorizontal(filterTreeHorizontalStyle, filterLayout);
  }

  if (!context.filterTreeViewPort) {
    context.filterTreeViewPort = {};
  }

  // init viewport management for filter tree
  for (const option of options) {
    if (option.values && isFilterOptionSupported(option.displayType)) {
      context.filterTreeViewPort[option.filterOptionId] = {
        loaded: 0,
        total: option.values.length,
        displayType: option.displayType,
      };
    }
  }

  const renderPromise = [];

  if (Object.keys(context.filterTreeViewPort).length > 0) {
    const isMobileStyle =
      ['style2', 'style2-fullwidth', 'style3', 'style3-fullwidth'].includes(
        filterTreeMobileStyle
      ) && isMobile(context.app.generalSettings.isTabletPortraitMax);
    const filterTreeParent = getFilterTreeParent(context);

    for (const [key, value] of Object.entries(context.filterTreeViewPort)) {
      if (value.loaded !== 0) continue;

      const keyEncodeMetaField = key.replace(/::/g, '__');
      const optionDomPrefix = isTabletPortraitMaxWidth()
        ? 'boost-sd_filter-tree-mobile'
        : 'boost-sd-filter-tree-column';
      const optionDom = context.document.querySelectorAll(
        // get specific filter option to avoid duplicate events because mobile/desktop runs at the same time
        `#${optionDomPrefix} #${keyEncodeMetaField}`
      );

      if (!optionDom.length) continue;

      const filterOptionWrapperDom = Array.from(optionDom).map((dom) =>
        dom.closest(`${FILTER_OPTION_SELECTOR}`)
      );

      const option = options.find((option) => option.filterOptionId === key);

      if (filterOptionWrapperDom && option) {
        renderPromise.push(
          ...filterOptionWrapperDom.map(async (target) => {
            const targetDom = target.querySelector(`#${keyEncodeMetaField}`);
            if (!targetDom) return;
            if (isFilterOptionSupported(value.displayType)) {
              const valuesLength = option.values.length;

              const displayAllValues = option.showMoreType === SHOW_MORE_TYPES.DISPLAY_ALL_VALUES;

              // display first load - support display all values
              const firstLoad =
                valuesLength < LIMIT_FIRST_LOAD || displayAllValues
                  ? valuesLength
                  : LIMIT_FIRST_LOAD;

              /**
               * display all values remove class scrollbar: FILTER_OPTION_CONTENT_INNER_SCROLL_BAR
               * Apply apply for filter tree vertical or mobile. (not apply for layout horizontal)
               * */
              if (
                displayAllValues &&
                (isMobile() || (!isMobile() && filterLayout === 'vertical'))
              ) {
                targetDom
                  .closest(`.${FILTER_OPTION_CONTENT_INNER}`)
                  ?.classList?.remove(FILTER_OPTION_CONTENT_INNER_SCROLL_BAR);
              }

              context.filterTreeViewPort[key] = {
                loaded: firstLoad,
                total: valuesLength,
                displayType: option.displayType,
              };

              // Each filter option render should be added to the task queue
              // requestIdleCallback(function renderFilterTreeMacroTask() {
              // });

              if (isMobileStyle) {
                bindingToolbarFilterMobileStyle(context, option);
              }

              bindEventSearchOption(context, option, targetDom, filterTreeParent);
              if (valuesLength > firstLoad) {
                renderLazyFilterOption(context, targetDom, option);
              }

              await renderFilterOption(context, targetDom, option, firstLoad);
            }
          })
        );
      }
    }
  }

  requestAnimationFrame(async () => {
    await Promise.all(renderPromise);
    if (context.oldDOM) {
      context.oldDOM.replaceWith(context.document);
      context.oldDOM = null;
      applyTranslationsToFilterOptions(context);
      if (context.onAfterDocumentChanged) {
        context.isRendering = false;
        context.onAfterDocumentChanged();
        const afterRenderFn =
          context.app.customization[context.blockType]?.afterRender ??
          context.app.customization.afterRender;
        afterRenderFn?.(context);
      }
    } else {
      applyTranslationsToFilterOptions(context);
      context.onFirstRender?.();
      blockFilterOpening(context);

      const stickyStatus = getStickyStatus(context);
      if (stickyStatus.stickyEnabled) {
        //enable sticky
        if (stickyStatus.stickyFilterOnMobile && isMobileWidth()) {
          await stickyMobile(context);
        }
        if (stickyFilterOnDesktop && !isMobileWidth()) {
          if (filterLayout === 'vertical') {
            await stickyVertical(context);
          } else if (filterLayout === 'horizontal') {
            await stickyHorizontal(context);
          }
        }
      }
    }
  });
}

/**
 * @type {import('./index.d').buildEventFilterOptionCollapsed}
 */
const buildEventFilterOptionCollapsed = (context, rawAction, metadata) => {
  // build desktop
  context
    .$(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}`)
    ?.setAttribute('data-metadata', metadata);
  context
    .$(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction} svg`)
    ?.setAttribute('data-metadata', metadata);
  context
    .$(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction} svg path`)
    ?.setAttribute('data-metadata', metadata);

  // build mobile
  context
    .$(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}`)
    ?.setAttribute('data-metadata', metadata);
  context
    .$(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction} svg`)
    ?.setAttribute('data-metadata', metadata);
  context
    .$(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction} svg path`)
    ?.setAttribute('data-metadata', metadata);
};

/**
 * @type {import('./index.d').removeDomFilterOptionIfEmpty}
 */

const removeDomFilterOptionIfEmpty = (context, rawAction, option) => {
  // build desktop

  if (Array.isArray(option.values) && option.values?.length === 0) {
    // remove dome filter option empty
    context
      .$(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}`)
      ?.closest('.boost-sd__filter-option')
      ?.remove();

    context
      .$(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}-target`)
      ?.closest('.boost-sd__filter-option')
      ?.remove();
  }
};

/**
 * @type {import('./index.d').buildAttributeDataFilterOptionListItem}
 */
const buildAttributeDataFilterOptionListItem = (context, rawAction, option) => {
  // build desktop
  context
    .$(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}`)
    ?.closest('.boost-sd__filter-option')
    ?.setAttribute('data-filterOptionId', option.filterOptionId);

  // build mobile
  context
    .$(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}-target`)
    ?.closest('.boost-sd__filter-option')
    ?.setAttribute('data-filterOptionId', option.filterOptionId);
};

/**
 * @type {import('./index.d').showHideSearchBoxOption}
 */
const showHideSearchBoxOption = (context, rawAction, option) => {
  // build Show Hide search option
  if (option.showSearchBoxFilterPC || option.showSearchBoxFilterMobile) {
    // if PC not show -> mobile show -> disabled search box desktop
    if (!option.showSearchBoxFilterPC) {
      // Remove dom input desktop
      context
        .$(
          `${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}-target ${FILTER_OPTION_SEARCH_OPTION_SELECTOR}`
        )
        ?.remove();
    }

    // if Mobile not show -> desktop show -> disabled search box mobile
    if (!option.showSearchBoxFilterMobile) {
      // Remove dom input mobile
      context
        .$(
          `${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}-target ${FILTER_OPTION_SEARCH_OPTION_SELECTOR}`
        )
        ?.remove();
    }
  }
};

/**
 * @type {import('./index.d').initFilterOptionCollapse}
 */
export function initFilterOptionCollapse(context) {
  const { options } = context.state?.filterTree || {};
  const { keepToggleState, filterLayout, filterTreeHorizontalStyle, filterTreeMobileStyle } =
    getFilterSettings(context);

  if (!options) return;

  const optionsActive = options.filter((option) => option.status !== 'disabled');

  const _isMobile = isMobile(context.app.generalSettings.breakpointtabletportraitmax);
  const _isDesktop = !_isMobile;

  watchCollapseStateChange(context);

  // remove class hidden in mobile style1 - FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS
  if (['style1', 'style3-fullwidth', 'style3'].includes(filterTreeMobileStyle)) {
    const wrapperItemsStyle = context?.document?.querySelectorAll(
      `${FILTER_MOBILE_WRAPPER_SELECTOR} .boost-sd__filter-option-itemwrapper`
    );

    if (wrapperItemsStyle?.length > 0) {
      wrapperItemsStyle?.forEach((wrapper) => {
        wrapper?.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
      });
    }
  }

  const [getCollapseState, setCollapseState] = context.useContextState(COLLAPSE_STATE, {});

  let _getCollapseState = {};

  // If keepToggleState = true then when init/ recall-api state will merge current state else nothing
  if (keepToggleState) {
    _getCollapseState = getCollapseState();
  }

  const [getFilter] = context.useContextState('filter', []);
  const selectedFilters = getFilter();

  optionsActive.forEach((option) => {
    requestIdleCallback(() => {
      const rawAction = `${option.filterOptionId.replace(/::/g, '__')}-toggle`;
      const metadata = JSON.stringify({
        action: {
          filterOptionCollapse: {
            actionId: rawAction,
          },
        },
      });

      removeDomFilterOptionIfEmpty(context, rawAction, option);
      buildAttributeDataFilterOptionListItem(context, rawAction, option);
      buildEventFilterOptionCollapsed(context, rawAction, metadata);
      showHideSearchBoxOption(context, rawAction, option);

      if (
        !isMobile(context.app.generalSettings.breakpointtabletportraitmax) &&
        filterLayout === 'vertical'
      ) {
        // append clear in filter option
        const filterOptionBtnClear = context.$(
          `${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}-target-label .boost-sd__button--clear`
        );

        if (filterOptionBtnClear) {
          const filterOptionSelected = selectedFilters.map((selected) => selected.data.key);

          if (!filterOptionBtnClear.dataset.metadata) {
            const metadata = JSON.stringify({
              action: {
                resetFilterOption: { key: option.filterOptionId },
                from: 'client',
              },
            });
            filterOptionBtnClear.setAttribute('data-metadata', metadata);
            filterOptionBtnClear.querySelector('span')?.setAttribute('data-metadata', metadata);
          }

          if (filterOptionSelected.includes(option.filterOptionId)) {
            filterOptionBtnClear.classList?.remove('boost-sd__g-hide');
          } else {
            filterOptionBtnClear.classList?.add('boost-sd__g-hide');
          }
        }
      }

      if (
        !isMobile(context.app.generalSettings.breakpointtabletportraitmax) &&
        filterLayout === 'horizontal'
      ) {
        // bind event to apply and reset button
        const applyId = simpleMd5(`${option.filterOptionId}-apply-button`);
        const clearId = simpleMd5(`${option.filterOptionId}-reset-button`);
        const metadataApply = JSON.stringify({
          action: {
            applyFilterOption: {
              key: option.filterOptionId,
              from: 'client',
            },
          },
        });

        const resetFilterOption = JSON.stringify({
          action: {
            resetFilterOption: {
              key: option.filterOptionId,
              from: 'client',
            },
          },
        });

        if (filterTreeHorizontalStyle !== 'style-expand') {
          const btnApply = context.$(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${applyId}`);
          const btnClear = context.$(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${clearId}`);

          if (btnApply && !btnApply.dataset.metadata)
            btnApply.setAttribute('data-metadata', metadataApply);
          if (btnClear && !btnClear.dataset.metadata)
            btnClear.setAttribute('data-metadata', resetFilterOption);
        }
      }
    });
  });

  // bind event for Apply and Clear All button in horizontal style-expand
  const isHorizontalStyleExpand =
    !isMobile(context.app.generalSettings.breakpointtabletportraitmax) &&
    filterLayout === 'horizontal' &&
    filterTreeHorizontalStyle === 'style-expand';

  if (isHorizontalStyleExpand) {
    const dom = context.$(`.boost-sd__filter-tree-horizontal-button`);
    if (dom) {
      dom.innerHTML =
        `
      <button id="boost-sd__apply-all-button" metadata='{"action": { "applyFilterOption": "applyAll" }}' type="button" class="boost-sd__button boost-sd__button--apply">
        <span class="boost-sd__button-text">` +
        context.app?.translation?.applyAll +
        `</span>
      </button>
      <button id="boost-sd__reset-all-button" metadata='{"action": { "clearAllFilter": "" }}' type="button" class="boost-sd__button boost-sd__button--clear">
          <span class="boost-sd__button-text">` +
        context.app?.translation?.clearAll +
        `</span>
      </button>
      `;
    }
  }

  // manager collapse/open based on state
  const collapseInitState = {};

  optionsActive?.forEach((option) => {
    const rawAction = `${option.filterOptionId.replace(/::/g, '__')}-toggle`;

    const targetId = `${rawAction}-target`;
    const targetIdLabel = `${rawAction}-target-label`;

    const actionId = rawAction;
    const isCollapsedHorizontal =
      _isDesktop && filterLayout === 'horizontal' && filterTreeHorizontalStyle !== 'style-expand';

    // init collapsed desktop vertical or mobile style1, style3, style3-fullwidth (Don't apply for mobile style2)
    if (
      (_isDesktop && filterLayout !== 'horizontal') ||
      (_isMobile && ['style1', 'style3-fullwidth', 'style3'].includes(filterTreeMobileStyle))
    ) {
      collapseInitState[actionId] = {
        actionId: actionId,
        filterLayout: filterLayout,
        targets: [
          {
            targetId: targetId, // id wrap filter option items
            classToggle: FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS,
            shouldAddClass:
              _isDesktop && option.isCollapsePC
                ? true
                : !_isDesktop && option.isCollapseMobile
                  ? true
                  : false,
          },
          {
            classToggle: 'boost-sd__filter-option-label--collapsed',
            targetId: targetIdLabel,
            shouldAddClass:
              _isDesktop && option.isCollapsePC
                ? true
                : !_isDesktop && option.isCollapseMobile
                  ? true
                  : false,
          },
        ],
      };
    }

    // Init collapsed horizontal style1, style2, style3 (! style-expand)
    if (isCollapsedHorizontal) {
      collapseInitState[actionId] = {
        actionId: actionId,
        filterLayout: filterLayout,
        targets: [
          {
            targetId: targetId,
            classToggle: FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS,
            shouldAddClass: true,
          },
          {
            classToggle: FILTER_OPTION_TITLE_OPENING_CLASS,
            targetId: targetIdLabel,
            shouldAddClass: false,
          },
        ],
      };
    }

    // Remove class hidden if template return have class hidden
    if (isHorizontalStyleExpand) {
      const filterTreeParent = getFilterTreeParent(context);
      context
        .$(`#${targetId}`, filterTreeParent)
        ?.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
    }
  });

  setCollapseState({
    ...collapseInitState,
    ..._getCollapseState,
  });

  // ✅ Add Escape key support to collapse all open filter options
  context.document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const focusedEl = document.activeElement;
      const filterBox = focusedEl?.closest('.boost-sd__filter-option');
      if (!filterBox) return;

      const actionId = filterBox.querySelector('.boost-sd__filter-option-title')?.id;
      if (!actionId) return;

      const [getCollapseState, setCollapseState] = context.useContextState(COLLAPSE_STATE, {});
      const collapseState = getCollapseState();

      const currentAction = collapseState[actionId];
      if (!currentAction || !currentAction.targets) return;

      const updatedTargets = currentAction.targets.map((target) => {
        return {
          ...target,
          shouldAddClass: target.classToggle === FILTER_OPTION_TITLE_OPENING_CLASS ? false : true,
        };
      });

      const updatedAction = {
        ...currentAction,
        targets: updatedTargets,
      };

      handleCollapse(context, updatedAction);
      setCollapseState({
        ...collapseState,
        [actionId]: updatedAction,
      });

      const toggleBtn = filterBox.querySelector('.boost-sd__filter-option-title');

      if (toggleBtn?.getAttribute('aria-expanded') === 'true') {
        toggleBtn.setAttribute('aria-expanded', 'false');
      }

      toggleBtn?.focus();
    }
  });
}

/**
 * @type {import('./index.d').watchCollapseStateChange}
 */
export function watchCollapseStateChange(context) {
  context.render(() => {
    const [getCollapseState] = context.useContextState(COLLAPSE_STATE, {});
    const _getCollapseState = getCollapseState();
    const keys = Object.keys(_getCollapseState);

    if (keys.length > 0) {
      keys.forEach((key) => {
        handleCollapse(context, _getCollapseState[key]);
      });
    }
  }, [COLLAPSE_STATE]);
}

/**
 * @type {import('./index.d').handleCollapse}
 */
export function handleCollapse(context, action) {
  if (!action) return; // cover horizontal with style = style-expand
  const filterTreeParent = getFilterTreeParent(context);

  const targets = action.targets;
  if (targets.length > 0) {
    targets.forEach((target) => {
      const targetElement = context.$(`#${target.targetId}`, filterTreeParent);
      const shouldAddClass = target.shouldAddClass;

      if (targetElement) {
        shouldAddClass
          ? targetElement.classList.add(target.classToggle)
          : targetElement.classList.remove(target.classToggle);
      }
    });
  }
}

/**
 * @type {import('./index.d').handleToggle}
 */
export function handleToggle(context, action, containerId = '') {
  if (!action) return; // cover horizontal with style = style-expand
  const targets = action.targets;
  if (targets.length > 0) {
    targets.forEach((target) => {
      const targetElement = context.$(`${containerId ? containerId + ' ' : ''}#${target.targetId}`);
      targetElement?.classList?.toggle(target.classToggle);
    });
  }
}

/**
 * Toggle class collapse
 * {
 *    actionId: string, Id of user click
 *    targets: [
 *      {
 *          classToggle: string, // class toggle
 *          shouldAddClass: boolean, // true -> add class, false -> remove class
 *          targetId: string // Id effect
 *      },
 *      ...
 *    ]
 * }
 *  @type {import('./index.d').filterOptionCollapse}
 */
export function filterOptionCollapse(context, action) {
  if (isMobile(context.app.generalSettings.isTabletPortraitMax)) {
    collapseMobileBtn(context, action);
  }

  const { filterLayout } = getFilterSettings(context);
  const [getCollapseState, setCollapseState] = context.useContextState(COLLAPSE_STATE, {});
  const _getCollapseState = getCollapseState();

  if (Object.keys(_getCollapseState)?.length === 0) return;

  // horizontal close filterOption opening
  if (filterLayout === 'horizontal') {
    Object.keys(_getCollapseState).forEach((key) => {
      const targets = _getCollapseState[key]?.targets || [];
      if (
        targets &&
        targets.length > 0 &&
        key !== action.actionId &&
        !targets[0].shouldAddClass &&
        targets[1]?.shouldAddClass
      ) {
        _getCollapseState[key] = getFilterOptionToggleState(context, _getCollapseState[key]);
      }
    });
  }

  // set toggle current filterOption
  _getCollapseState[action.actionId] = getFilterOptionToggleState(
    context,
    _getCollapseState[action.actionId]
  );

  _getCollapseState[action.actionId] && setCollapseState(_getCollapseState);
}

/**
 * @type {import('./index.d').getFilterOptionToggleState}
 */
export function getFilterOptionToggleState(context, currentAction) {
  const _getFilterTreeParent = getFilterTreeParent(context);
  let targets = currentAction?.targets || [];
  if (targets.length > 0) {
    targets = targets.map((target) => {
      const targetElement = context.$(`#${target.targetId}`, _getFilterTreeParent);
      if (targetElement) {
        const buttonFilterOptionToggle = targetElement.querySelector(
          '.boost-sd__filter-option-title'
        );
        toggleAriaExpanded(buttonFilterOptionToggle);

        return {
          ...target,
          shouldAddClass: !target.shouldAddClass,
        };
      }

      return target;
    });

    currentAction.targets = targets;
  }

  return currentAction;
}

/**
 * @type {import('./index.d').closeFilterOptionHorizontalOpening}
 */
export function closeFilterOptionHorizontalOpening(context) {
  // horizontal close filterOption opening
  const [getCollapseState, setCollapseState] = context.useContextState(COLLAPSE_STATE, {});
  const _getCollapseState = getCollapseState();
  Object.keys(_getCollapseState).forEach((key) => {
    const targets = _getCollapseState[key]?.targets || [];
    if (targets && targets.length > 0 && !targets[0].shouldAddClass && targets[1]?.shouldAddClass) {
      _getCollapseState[key] = getFilterOptionToggleState(context, _getCollapseState[key]);
    }
  });
  setCollapseState(_getCollapseState);
}

/**
 * @type {import('./index.d').determineScrollTarget}
 * */
const determineScrollTarget = (context) => {
  const { filterLayout, stickyFilterOnDesktop, filterTreeHorizontalStyle, stickyFilterOnMobile } =
    getFilterSettings(context);

  if (stickyFilterOnMobile && isMobileWidth()) {
    return {
      isFilter: true,
      scrollToElement: '.boost-sd__toolbar-container',
    };
  }

  if (stickyFilterOnDesktop && !isMobileWidth() && filterLayout === 'horizontal') {
    if (filterTreeHorizontalStyle !== 'style-expand') {
      const refineByElement = document.querySelector('div[class^="boost-sd__refine-by"]');
      const havingRefineBy = refineByElement && refineByElement.textContent.trim().length > 0;
      if (havingRefineBy) {
        return { isFilter: true, scrollToElement: 'div[class^="boost-sd__refine-by"]' };
      } else {
        return { isFilter: true };
      }
    } else {
      return { isFilter: true, scrollToElement: '.boost-sd-container' };
    }
  } else {
    const refineByElement = document.querySelector('#bc-refine-by-mobile');
    const havingRefineBy = refineByElement && refineByElement.textContent.trim().length > 0;
    if (havingRefineBy) {
      return { isFilter: true, scrollToElement: '#bc-refine-by-mobile' };
    } else {
      return { isFilter: true, behavior: 'auto' };
    }
  }
};

/**
 * @type {import('./index.d').applyFilterOption}
 */
export function applyFilterOption(context, action) {
  if (action === 'applyAll') {
    // force close filter Tree
    const buttonToggle = document.querySelector(
      '.boost-sd__filter-tree-toggle-button.boost-sd__filter-tree-toggle-button--expand'
    );
    buttonToggle?.click();
  }
  const scrollOptions = determineScrollTarget(context);
  scrollToTop(scrollOptions);

  closeFilterOptionHorizontalOpening(context);
  resetFilterRequest(context);

  // reset selecting when apply
  const [, setItemSelecting] = context.useContextState('filterOptionItemSelecting', {});
  setItemSelecting({});
}

/**
 * @type {import('./index.d').resetFilterOption}
 */
export function resetFilterOption(context, action) {
  const { key } = action;
  const [getFilterTreeOption] = context.useContextState('filterTree', {});
  const { isShortenUrlParam } = getFilterSettings(context);
  const options = getFilterTreeOption()?.options;
  const keys = key === getCollectionFilterOptionId(options) ? [key, FILTER_TAG_KEY] : [key];

  const [getFilterTree, setFilterTree] = context.useContextState('filter', []);
  const filterTree = getFilterTree();
  const newFilterTree = filterTree.filter((ft) => ft && !keys.includes(ft.data.key));
  setFilterTree(newFilterTree);

  closeFilterOptionHorizontalOpening(context);

  deleteQueryParamsHistory(keys, {
    preventPushHistory: false,
    isShortenUrlParam,
    urlScheme: getUrlScheme(),
  });
  resetFilterRequest(context);
}

/**
 * @type {import('./index.d').expandCollapseMultiLevel}
 */
export function expandCollapseMultiLevel(context, action) {
  const { keepToggleState } = getFilterSettings(context);

  if (keepToggleState) {
    const [getCollapseStateMultipleLevel, setCollapseStateMultipleLevel] = context.useContextState(
      'collapseStateMultipleLevel',
      {}
    );
    const _getCollapseStateMultipleLevel = getCollapseStateMultipleLevel();

    if (_getCollapseStateMultipleLevel[action.actionId]) {
      action = _getCollapseStateMultipleLevel[action.actionId];
    }

    // toggle targets
    if (action?.targets?.length > 0) {
      action.targets = action.targets.map((target) => ({
        ...target,
        shouldAddClass: !target.shouldAddClass,
      }));
    }

    setCollapseStateMultipleLevel({
      ..._getCollapseStateMultipleLevel,
      [action.actionId]: action,
    });
  }

  handleToggle(context, action, isMobile() ? '#boost-sd_filter-tree-mobile' : '');
}

/**
 * @type {import('./index.d').collapseMobileBtn}
 */
export const collapseMobileBtn = (context, action) => {
  const filterTreeParent = getFilterTreeParent(context);
  const targetElement = context.$(
    `#${action.elementId || `${action.actionId}-target`}`,
    filterTreeParent
  );
  const { filterTreeMobileStyle } = getFilterSettings(context);
  const isMobileStyle2 =
    filterTreeMobileStyle === 'style2' || filterTreeMobileStyle === 'style2-fullwidth';
  const isMobileStyle3 =
    filterTreeMobileStyle === 'style3' || filterTreeMobileStyle === 'style3-fullwidth';

  if (filterTreeMobileStyle !== 'style1') {
    const refineByTitle = context.$(
      '.boost-sd__filter-tree-vertical-toolbar-middle',
      filterTreeParent
    );
    const currentTitle = context.$(`#${action.actionId}`);

    if (refineByTitle && currentTitle) {
      refineByTitle.innerHTML = currentTitle.firstChild?.textContent?.replace(/\\n|\s/, '');
    }
  }

  if (isMobileStyle2) {
    // change clearAll to clear if current filterOptionId filtered
    const domClearAllButton = context.$(FILTER_TREE_MOBILE_BUTTON_CLEAR);

    const selectedFilter = getSelectedFilter(context);

    // Get original filterOptionId (with ::) from DOM data attribute, not from encoded actionId
    const domFilterOption = targetElement?.closest('.boost-sd__filter-option');
    const filterOptionId = domFilterOption?.dataset?.filteroptionid;
    const existKey = selectedFilter.find((item) => item.metaData?.key === filterOptionId);

    const [, setFilterOptionIdMobileSelected] = context.useContextState(
      'filterOptionIdMobileSelected',
      ''
    );

    filterOptionId && setFilterOptionIdMobileSelected({ level: 2, key: filterOptionId });

    if (existKey) {
      const clearButton = buttonClearOrClearAll(context, 'clear', filterOptionId);
      domClearAllButton.replaceWith(clearButton);
    } else if (domClearAllButton) {
      domClearAllButton?.classList?.add('boost-sd__g-hide');
    }
  }

  if (isMobileStyle3) {
    const selectedFilter = getSelectedFilter(context);

    if (selectedFilter.length > 0) {
      const clearAllButton = buttonClearOrClearAll(context, 'clearAll');

      if (!context.$(FILTER_TREE_MOBILE_BUTTON_CLEAR)) {
        const toolBarMobile = context.$(FILTER_TREE_MOBILE_TOOL_BAR);
        toolBarMobile?.append(clearAllButton);
      } else {
        context.$(FILTER_TREE_MOBILE_BUTTON_CLEAR)?.replaceWith(clearAllButton);
      }
    }
  }

  Array.from(
    context.$(FILTER_MOBILE_WRAPPER_SELECTOR)?.querySelectorAll('.boost-sd__filter-option-label')
  )?.forEach((e) => {
    if (isMobileStyle2) {
      e?.classList?.toggle('boost-sd__filter-option-label--hidden');
    }
  });

  if (targetElement) {
    const buttonFilterOptionToggle = targetElement
      ?.closest('.boost-sd__filter-option')
      ?.querySelector('.boost-sd__filter-option-title');
    toggleAriaExpanded(buttonFilterOptionToggle);

    if (filterTreeMobileStyle === 'style2') {
      context.$(FILTER_MOBILE_BUTTON_CLOSE_CLASS)?.classList?.remove(FILTER_MOBILE_BUTTON_HIDDEN);
    }

    if (isMobileStyle2) {
      targetElement.classList?.toggle(FILTER_MOBILE_FULL_HEIGHT_CLASS);
      targetElement.classList?.toggle(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
      targetElement.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS);
      context.$(FILTER_MOBILE_BUTTON_CLOSE_CLASS)?.classList?.add(FILTER_MOBILE_BUTTON_BACK);
      context.$(FILTER_MOBILE_BUTTON_CLOSE_CLASS)?.classList?.remove(FILTER_MOBILE_BUTTON_CLOSE);
    }

    const domList = context.$('.boost-sd__filter-option-listitem', targetElement);

    if (domList) {
      const domFilterOption = domList?.closest('.boost-sd__filter-option');
      const filterOptionId = domFilterOption?.dataset?.filteroptionid;
      const option = getFilterOptionById(context, filterOptionId);
      option && renderFullyFilterOption(context, domList, option);
    }
  }
};

/**
 * @type {import('./index.d').getFilterOptionById}
 */
const getFilterOptionById = (context, id) => {
  const { useContextState } = context;
  const [getFilterTreeOption] = useContextState('filterTree', {});
  const options = getFilterTreeOption().options;
  return options.find((op) => op.filterOptionId === id);
};

/**
 * @type {import('./index.d').backFilterTreeMobile}
 */
export const backFilterTreeMobile = (context) => {
  const { filterTreeMobileStyle } = getFilterSettings(context);

  if (filterTreeMobileStyle !== 'style1') {
    const refineByMobileTitle = context.$('.boost-sd__filter-tree-vertical-toolbar-middle');

    if (refineByMobileTitle) {
      refineByMobileTitle.innerHTML = translateByKey(context, 'refineMobile', 'Refine by');
    }
  }

  // check if filtering - show button clear All
  if (
    ['style2', 'style2-fullwidth', 'style3', 'style3-fullwidth'].includes(filterTreeMobileStyle)
  ) {
    const selectedFilter = getSelectedFilter(context);
    if (selectedFilter.length > 0) {
      const btnClearAll = buttonClearOrClearAll(context, 'clearAll');

      if (!context.$(FILTER_TREE_MOBILE_BUTTON_CLEAR)) {
        const toolBarMobile = context.$(FILTER_TREE_MOBILE_TOOL_BAR);
        toolBarMobile.append(btnClearAll);
      } else {
        context.$(FILTER_TREE_MOBILE_BUTTON_CLEAR).replaceWith(btnClearAll);
      }
    }

    // set level1 show button clear All
    const [, setFilterOptionIdMobileSelected] = context.useContextState(
      'filterOptionIdMobileSelected',
      ''
    );
    setFilterOptionIdMobileSelected({ level: 1 });
  }

  if (filterTreeMobileStyle === 'style2') {
    context.$(FILTER_MOBILE_BUTTON_BACK_CLASS)?.classList?.add(FILTER_MOBILE_BUTTON_HIDDEN);
  }
  context.$(FILTER_MOBILE_BUTTON_BACK_CLASS)?.classList?.add(FILTER_MOBILE_BUTTON_CLOSE);
  context.$(FILTER_MOBILE_BUTTON_BACK_CLASS)?.classList?.remove(FILTER_MOBILE_BUTTON_BACK);

  Array.from(
    context.$(FILTER_MOBILE_WRAPPER_SELECTOR)?.querySelectorAll('.boost-sd__filter-option-label')
  ).forEach((targetElement) =>
    targetElement?.classList?.remove('boost-sd__filter-option-label--hidden')
  );

  Array.from(
    context.$(FILTER_MOBILE_WRAPPER_SELECTOR)?.querySelectorAll(FILTER_MOBILE_FULL_HEIGHT_SELECTOR)
  ).forEach((targetElement) => {
    targetElement?.classList?.remove(FILTER_MOBILE_FULL_HEIGHT_CLASS);
    targetElement?.classList?.add(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
    const buttonFilterOptionToggle = targetElement
      ?.closest('.boost-sd__filter-option')
      ?.querySelector('.boost-sd__filter-option-title');
    toggleAriaExpanded(buttonFilterOptionToggle);
  });
};

/**
 * @type {import('./index.d').getFilterSettings}
 */
export const getFilterSettings = (context) => {
  if (!context) {
    console.error('context getFilterSettings null');
    return {};
  }

  // swatch_settings get from filterSettings app admin
  return {
    ...(context?.templateSettings?.filterSettings || {}),
    swatch_settings: context?.app?.filterSettings?.swatch_settings,
  };
};

/**
 * @type {import('./index.d').toggleFilterTreeButtonDesktop}
 */
export const toggleFilterTreeButtonDesktop = (context, filterTreeParent) => {
  const {
    filterLayout,
    filterTreeVerticalStyle,
    stickyFilterOnDesktop,
    filterTreeHorizontalStyle,
  } = getFilterSettings(context);
  const [getFilterTreeDesktopOpening, setFilterTreeDesktopOpening] = context.useContextState(
    FILTER_TREE_STATE_KEY.DESKTOP_OPENING,
    false
  );
  const _currentStateOpening = getFilterTreeDesktopOpening();

  if (filterLayout === 'horizontal') {
    // if style Show all filter options
    const styleShowAllOptionSelector =
      '#boost-sd__filter-tree-wrapper .boost-sd__filter-tree-horizontal';
    const targetElement = context.$(styleShowAllOptionSelector, filterTreeParent);

    if (_currentStateOpening) {
      targetElement?.classList?.add('boost-sd__filter-tree-horizontal--hidden');
    } else {
      targetElement?.classList?.remove('boost-sd__filter-tree-horizontal--hidden');
      if (stickyFilterOnDesktop && !isMobileWidth()) {
        // Get merged configuration for horizontal sticky calls
        const stickyConfig = getStickyConfig('horizontal', filterTreeHorizontalStyle);
        loadStickyFilterTreeHorizontalPanel(context, stickyConfig.panel);
        loadStickyFilterTreeHorizontalContent(context, stickyConfig.content);
      }
    }

    toggleFilterTreeIcon(context);
    setFilterTreeDesktopOpening(!_currentStateOpening);
  } else {
    // if style Collapsed - Expand
    if (filterTreeVerticalStyle === 'style-expand') {
      const styleCollapseExpandSelector = '.boost-sd__filter-tree-vertical';
      const targetElement = context.$(styleCollapseExpandSelector, filterTreeParent);
      if (_currentStateOpening) {
        targetElement?.classList?.add('boost-sd__filter-tree-vertical--hidden');
      } else {
        targetElement?.classList?.remove('boost-sd__filter-tree-vertical--hidden');
        if (stickyFilterOnDesktop && !isMobileWidth()) {
          // Get merged configuration for vertical expand sticky call
          const { filterTreeVerticalStyle } = getFilterSettings(context);
          const stickyConfig = getStickyConfig('vertical', filterTreeVerticalStyle);
          loadStickyFilterTreeExpandContent(context, stickyConfig.content);
        }
      }
      toggleFilterTreeIcon(context);
      setFilterTreeDesktopOpening(!_currentStateOpening);
    }

    // if style Off canvas - Left side
    if (filterTreeVerticalStyle === 'style-off-canvas') {
      const styleOffCanvasSelector =
        '#boost-sd__filter-tree-wrapper #boost-sd__filter-tree-vertical--style-off-canvas';

      const targetElement = context.$(styleOffCanvasSelector, filterTreeParent);
      if (_currentStateOpening) {
        targetElement?.classList?.add('boost-sd__filter-tree-vertical--hidden');
      } else {
        targetElement?.classList?.remove('boost-sd__filter-tree-vertical--hidden');
      }
      toggleFilterTreeIcon(context);
      setFilterTreeDesktopOpening(!_currentStateOpening);
      document.body.classList.add('boost-sd__g-no-scroll');
    }
  }
};

/**
 * @type {import('./index.d').initCurrencyForFilterPrice}
 */
export function initCurrencyForFilterPrice(context) {
  const [getFilterTree] = context.useContextState('filterTree', {});
  const options = getFilterTree()?.options;
  const { showCurrencyCodes } =
    context?.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};
  const moneyFormat = showCurrencyCodes
    ? context?.app?.shop?.money_format_with_currency
    : context?.app?.shop?.money_format;

  if (options?.length > 0) {
    options.forEach((option) => {
      if (['price', 'variants_price'].includes(option.filterType)) {
        option.moneyFormatValue = stripHtml(moneyFormat);
      }
    });
  }
}

/**
 * @type {import('./index.d').getCollectionFilterOptionId}
 */
export const getCollectionFilterOptionId = (options) => {
  if (options?.length > 0) {
    return options.filter((option) => option.filterType === 'collection')?.[0]?.filterOptionId;
  }

  return 'pf_c_collection';
};

/**
 * @type {import('./index.d').isEnterOrSpaceKeyPressed}
 */
export function isEnterOrSpaceKeyPressed(context) {
  const [getFilterKeyCode] = context.useContextState(FILTER_TREE_STATE_KEY.KEY_CODE, '');
  const keyCode = getFilterKeyCode();
  const isEnterOrSpace = keyCode === 32 || keyCode === 13;
  return isEnterOrSpace;
}

/**
 * @type {import('./index.d').toggleAriaExpanded}
 */
export function toggleAriaExpanded(element) {
  if (element) {
    const isExpanded = element.getAttribute('aria-expanded') === 'true';
    element.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
  }
}
